import React from "react";
import { Link } from "react-router-dom";
import PublicLayout from "./PublicLayout";
import { ArrowUpRight, ArrowRight, Circle } from "lucide-react";

const HOSES = [
  { code: "SAE 100R1", desc: "One-wire braid, medium-pressure hydraulic", wp: "88–225 bar", temp: "-40 to +100°C" },
  { code: "SAE 100R2", desc: "Two-wire braid, high-pressure hydraulic", wp: "88–415 bar", temp: "-40 to +100°C" },
  { code: "SAE 100R7", desc: "Thermoplastic single-wire, hydraulic", wp: "70–350 bar", temp: "-40 to +100°C" },
  { code: "SAE 100R9", desc: "Four-spiral wire, extra-high pressure", wp: "280–420 bar", temp: "-40 to +100°C" },
  { code: "SAE 100R12", desc: "Four-spiral wire, high pressure", wp: "280 bar", temp: "-40 to +121°C" },
  { code: "SAE 100R13", desc: "Multi-spiral wire, ultra-high pressure", wp: "345 bar", temp: "-40 to +121°C" },
  { code: "SAE 100R15", desc: "Four/six spiral wire, constant working pressure", wp: "420 bar", temp: "-40 to +121°C" },
  { code: "EN 856 4SP", desc: "Four-spiral wire, extreme abrasion", wp: "350–450 bar", temp: "-40 to +100°C" },
  { code: "EN 856 4SH", desc: "Heavy-duty four-spiral wire", wp: "420 bar", temp: "-40 to +100°C" },
];

const FITTINGS = ["BSP · British Standard Parallel", "BSPT · British Standard Taper", "JIC 37° Flare", "ORFS · O-Ring Face Seal", "SAE 45° Flare", "SAE Flange Code 61 / 62", "Metric DIN 24° Cone", "NPT · National Pipe Taper"];

const ACCESSORIES = [
  { title: "Ferrules", body: "One-piece and skive-type ferrules for R1/R2/R9/R12 assemblies." },
  { title: "PTFE Hoses", body: "Chemical-resistant, high-temperature applications." },
  { title: "Suction & Return", body: "Low-pressure large-bore hoses for tank returns." },
  { title: "Spiral Guards", body: "Polyethylene spiral wrap to protect hose OD from abrasion." },
  { title: "Fire Sleeves", body: "Silicone-coated firesleeve for engine bays and hot zones." },
  { title: "Custom Assemblies", body: "Made-to-print assemblies with your part number and drawing." },
];

export default function Products() {
  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden" data-testid="products-hero">
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.unsplash.com/photo-1682268294076-3af10a9b8c29?auto=format&fit=crop&w=2000&q=80" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-32 md:py-40">
          <div className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#E11D2A] mb-6">— Product Catalogue</div>
          <h1 className="font-industrial uppercase tracking-tighter text-6xl md:text-8xl lg:text-9xl leading-[0.85] text-white max-w-4xl" data-testid="products-h1">
            Precision<br />
            engineered.<br />
            <span className="text-[#E11D2A]">Built to last.</span>
          </h1>
          <p className="mt-8 text-white/70 text-lg max-w-2xl">
            A complete range of hydraulic hoses, fittings, ferrules and accessories — assembled and tested in-house.
          </p>
        </div>
      </section>

      {/* Hoses table */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid="products-hoses">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
          <div>
            <div className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">01 / Hydraulic Hoses</div>
            <h2 className="font-industrial uppercase tracking-tight text-4xl md:text-5xl text-white">SAE & EN Series</h2>
          </div>
          <div className="font-tech text-[10px] uppercase tracking-[0.25em] text-white/40 max-w-xs text-right">
            All hoses conform to their respective SAE J517 or EN standards. Custom lengths & fittings available.
          </div>
        </div>

        <div className="border border-white/10">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 bg-[#0a0a0a] font-tech text-[10px] uppercase tracking-[0.25em] text-white/50">
            <div className="col-span-3">Standard</div>
            <div className="col-span-5">Description</div>
            <div className="col-span-2">Working Pressure</div>
            <div className="col-span-2">Temp Range</div>
          </div>
          {HOSES.map((h, i) => (
            <div key={h.code} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-5 border-b last:border-b-0 border-white/10 hover:bg-[#111] transition-colors group" data-testid={`hose-row-${i}`}>
              <div className="md:col-span-3">
                <div className="font-tech text-white font-medium text-sm">{h.code}</div>
              </div>
              <div className="md:col-span-5 text-white/70 text-sm">{h.desc}</div>
              <div className="md:col-span-2 font-tech text-white/80 text-sm">{h.wp}</div>
              <div className="md:col-span-2 font-tech text-white/80 text-sm">{h.temp}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Fittings */}
      <section className="bg-[#080808] border-y border-white/10" data-testid="products-fittings">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">02 / Fittings & Adaptors</div>
            <h2 className="font-industrial uppercase tracking-tight text-4xl md:text-5xl text-white leading-none">
              Every thread. <span className="text-[#E11D2A]">Every seal.</span>
            </h2>
            <p className="mt-6 text-white/60 leading-relaxed">
              Precision-forged carbon steel and stainless fittings in every standard used in Indian and global hydraulics. Zinc-nickel plated for corrosion resistance.
            </p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
            {FITTINGS.map((f, i) => (
              <div key={f} className="flex items-center gap-4 px-5 py-4 border border-white/10 hover:border-[#E11D2A] transition-colors group" data-testid={`fitting-${i}`}>
                <div className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/30 min-w-[24px]">{String(i + 1).padStart(2, "0")}</div>
                <Circle size={6} className="text-[#E11D2A] fill-current shrink-0" />
                <div className="font-tech text-sm text-white">{f}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accessories */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid="products-accessories">
        <div className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">03 / Accessories & Consumables</div>
        <h2 className="font-industrial uppercase tracking-tight text-4xl md:text-5xl text-white mb-12">Around the assembly.</h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ACCESSORIES.map((a, i) => (
            <div key={a.title} className="p-8 border border-white/10 hover:border-[#E11D2A] transition-colors group" data-testid={`accessory-${i}`}>
              <div className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/30 mb-6">{String(i + 1).padStart(2, "0")} / {String(ACCESSORIES.length).padStart(2, "0")}</div>
              <div className="font-industrial uppercase text-2xl text-white mb-3 tracking-tight">{a.title}</div>
              <p className="text-white/60 text-sm leading-relaxed">{a.body}</p>
              <ArrowUpRight size={18} className="mt-6 text-white/30 group-hover:text-[#E11D2A] group-hover:translate-x-1 -translate-y-0.5 transition-all" />
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 py-24" data-testid="products-cta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="font-industrial uppercase tracking-tight text-4xl md:text-5xl text-white leading-none">
              Can't find your spec?<br /><span className="text-[#E11D2A]">Send us the drawing.</span>
            </h2>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-3 px-7 py-4 bg-[#E11D2A] hover:bg-[#C81622] transition-colors font-tech text-xs uppercase tracking-[0.25em]" data-testid="products-cta-link">
            Request a Quote <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
